import type { Event } from '@sentry/types'

/**
 * Suppresses errors caused by Kundo's embedded widget, specifically:
 * - Unsafe use of `new URL(undefined)`
 * - Triggered by popstate handling inside their shadow DOM
 * - Results in `Failed to construct 'URL'` or similar messages
 *
 * These errors are known, harmless, and not actionable on our side.
 */
export function shouldIgnoreKundoError(event: Event): boolean {
  const errorMessage = event.exception?.values?.[0]?.value || ''
  const frames = event.exception?.values?.[0]?.stacktrace?.frames || []

  return (
    (errorMessage.includes('Failed to construct') ||
      errorMessage.includes('"undefined" cannot be parsed as a URL') ||
      errorMessage.includes('Invalid URL')) &&
    frames.some((frame) => {
      const file = frame.filename || ''
      const func = frame.function || ''
      return (
        file.includes('embed') ||
        file.includes('helpers.js') ||
        file.includes('events.ts') ||
        func.includes('capturePopState') ||
        func.includes('anonymous')
      )
    })
  )
}

/**
 * Suppresses invariant routing errors caused by redundant navigations.
 *
 * This typically occurs when router.push() or window.location.href
 * is called with the exact current URL — often from third-party attribution scripts
 * or unintended user navigation behavior.
 *
 * These are benign and do not indicate a broken user experience.
 */
export function shouldIgnoreInvariantNavigationError(event: Event): boolean {
  const message = event.exception?.values?.[0]?.value || ''
  return message.includes('Invariant: attempted to hard navigate to the same URL')
}

/**
 * Suppresses JavaScript engine errors caused by proxying
 * read-only properties like `navigator.javaEnabled`.
 *
 * These are triggered by their fingerprinting or bot detection logic,
 * are non-breaking for users, and cannot be fixed on our side.
 */
export function shouldIgnoreJavaEnabledError(event: Event): boolean {
  const message = event.exception?.values?.[0]?.value || ''
  return message.includes("property 'javaEnabled'") && message.includes('proxy target')
}

/**
 * Suppresses noisy unhandled promise rejections that are either:
 * - Caused by rejected promises without an actual Error object (e.g., plain strings, undefined)
 * - Known issues with frameworks or third-party code triggering invalid TypeErrors in rejection
 *
 * These commonly appear as:
 * - "Non-Error promise rejection captured with value: <value>"
 * - "Unhandled Rejection (TypeError): Cannot read properties of undefined"
 *
 * Reference: https://github.com/getsentry/sentry-javascript/issues/3440
 *
 * These errors are typically not actionable, often occur in unrelated async behavior,
 * and are surfaced inconsistently due to how browsers and Sentry handle non-Error rejections.
 */
export function shouldIgnoreUnhandledRejectionError(event: Event): boolean {
  const errorMessage = event.exception?.values?.[0]?.value || ''
  return (
    errorMessage.includes('Non-Error promise rejection captured with value') ||
    errorMessage.includes('Unhandled Rejection (TypeError): Cannot read properties of undefined')
  )
}

/**
 * Suppresses errors related to invalid calls to `runtime.sendMessage`
 * in the context of Chrome extensions.
 *
 * These errors are often triggered by third-party libraries or
 * browser extensions and are not actionable on our side.
 */
export function shouldIgnoreExtensionMessagingError(event: Event): boolean {
  const msg = event.exception?.values?.[0]?.value || ''
  return msg.includes('Invalid call to runtime.sendMessage')
}
